// src/pages/admin/ClinicJournalGroup.jsx
import { useMemo } from "react";
import { Button, DataTable } from "@common";
import { formatMoney } from "@utils/format";
import { isApprovalApproved } from "@utils/associateFee";

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Per-clinic column totals shown as a chip strip above the table.
// Each entry maps to a column key on the journal entry table.
const CLINIC_TOTAL_CONFIG = {
  dds_draw: { type: "money", label: "DDS Draw", badge: "bg-palette-emerald-badge" },
  production: { type: "money", label: "Production", badge: "bg-palette-sky-badge" },
  collection: { type: "money", label: "Collection", badge: "bg-palette-sky-badge" },
  lab_cost: { type: "money", label: "Lab", badge: "bg-palette-slate-secondary" },
  material_cost: { type: "money", label: "Material", badge: "bg-palette-slate-secondary" },
  invisalign_cost: { type: "money", label: "Invisalign", badge: "bg-palette-slate-secondary" },
  anesthesia_fee: { type: "money", label: "Anesthesia", badge: "bg-palette-slate-secondary" },
  anesthesiologist_fee: {
    type: "money",
    label: "Anesthesiologist",
    badge: "bg-palette-slate-secondary",
  },
  facility_fee: { type: "money", label: "Facility", badge: "bg-palette-slate-secondary" },
  botox: { type: "money", label: "Botox", badge: "bg-palette-slate-secondary" },
  production_adjustment: {
    type: "money",
    label: "Production Adj",
    badge: "bg-palette-amber-badge",
  },
  collection_adjustment: {
    type: "money",
    label: "Collection Adj",
    badge: "bg-palette-amber-badge",
  },
  other_profit: { type: "money", label: "Other Profit", badge: "bg-palette-sky-badge" },
  other_deduction: { type: "money", label: "Other Deduction", badge: "bg-palette-slate-secondary" },
  insurance: { type: "money", label: "Insurance", badge: "bg-palette-slate-secondary" },
  hours_worked: { type: "number", decimals: 2, label: "Hours", badge: "bg-palette-sky-badge" },
};

function getRowValue(row, key) {
  if (key === "dds_draw") return toNum(row?.breakdown?.dds_draw);
  if (key === "other_profit") {
    const tiers = Array.isArray(row?.other_profit) ? row.other_profit : [];
    return tiers.reduce((s, t) => s + toNum(t?.profit), 0);
  }
  if (key === "other_deduction") {
    const tiers = Array.isArray(row?.other_deduction) ? row.other_deduction : [];
    return tiers.reduce((s, t) => {
      const explicit = Number(t?.amount);
      if (Number.isFinite(explicit) && explicit !== 0) return s + explicit;
      if (t?.hour_rate !== undefined || t?.hours !== undefined) {
        return s + (Number(t?.hour_rate) || 0) * (Number(t?.hours) || 0);
      }
      return s + toNum(t?.profit);
    }, 0);
  }
  return toNum(row?.[key]);
}

function formatTotal(key, total) {
  const cfg = CLINIC_TOTAL_CONFIG[key];
  if (!cfg) return null;
  if (cfg.type === "money") return formatMoney(total ?? 0);
  return Number(total ?? 0).toFixed(cfg.decimals ?? 0);
}

export default function ClinicJournalGroup({
  clinic,
  clinicRows,
  existingEntries,
  justCreated,
  isSearching,
  columnsWithSelect,
  onCreateJournalEntry,
  isCreating,
  canEdit = true,
}) {
  const allApproved = clinicRows.every((r) => isApprovalApproved(r.admin_approval));
  const pendingCount = clinicRows.filter((r) => !isApprovalApproved(r.admin_approval)).length;
  const hasExisting = existingEntries.length > 0;

  // Compute per-clinic totals: a combined sum across all rows AND
  // separate DDS-only / HYG-only sub-totals. Rows of any other type
  // are excluded from the split but still counted in the combined sum.
  const columnTotals = useMemo(() => {
    const totals = {};
    Object.keys(CLINIC_TOTAL_CONFIG).forEach((key) => {
      const byType = { ALL: 0, DDS: 0, HYG: 0 };
      for (const row of clinicRows) {
        const v = getRowValue(row, key);
        byType.ALL += v;
        const t = String(row?.practitioner_type || "").toUpperCase();
        if (t === "DDS" || t === "HYG") byType[t] += v;
      }
      totals[key] = byType;
    });
    return totals;
  }, [clinicRows]);

  // Render the totals as a separate strip that sits ABOVE the table's
  // own thead. We mirror the table's column widths so each pill stack
  // lines up with its column. Using a real <table> means widths obey
  // the same layout rules the DataTable below uses.
  const hasAnyTotal = useMemo(
    () =>
      Object.values(columnTotals).some(
        (t) =>
          Math.abs(t?.ALL ?? 0) >= 0.005 ||
          Math.abs(t?.DDS ?? 0) >= 0.005 ||
          Math.abs(t?.HYG ?? 0) >= 0.005,
      ),
    [columnTotals],
  );

  return (
    <div className="rounded-lg border border-palette-gray-divider bg-white">
      <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-palette-gray-divider bg-palette-gray-soft rounded-t-lg">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-base font-semibold text-palette-gray-text">{clinic}</span>
          <span className="text-xs text-palette-gray-secondary">
            ({clinicRows.length} practitioner{clinicRows.length !== 1 ? "s" : ""})
          </span>
          {!allApproved && (
            <span className="text-xs text-palette-amber-heading bg-palette-amber-muted rounded px-2 py-0.5">
              {pendingCount} pending approval
            </span>
          )}
        </div>
        {hasExisting ? (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {existingEntries.map((e, i) => {
              const label = e.tranid ?? e.transactionnumber ?? "\u2014";
              const content = (
                <>
                  <svg
                    className="w-4 h-4 text-palette-emerald-badge"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  {e.trandate ? `${e.trandate} \u00b7 ` : ""}
                  {label}
                </>
              );
              const className =
                "inline-flex items-center gap-1 text-sm font-semibold text-palette-emerald-text bg-palette-emerald-muted border border-palette-emerald-ring rounded-md px-3 py-1 shadow-sm ring-1 ring-palette-emerald-border";
              return e.link ? (
                <a
                  key={e.id ?? e.tranid ?? i}
                  href={e.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${className} hover:bg-palette-emerald-border hover:underline`}
                  title="Open in NetSuite"
                >
                  {content}
                </a>
              ) : (
                <span
                  key={e.id ?? e.tranid ?? i}
                  className={className}
                  title="Existing NetSuite journal entry"
                >
                  {content}
                </span>
              );
            })}
          </div>
        ) : justCreated ? (
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-palette-emerald-hover bg-palette-emerald-muted border border-palette-emerald-ring rounded-md px-3 py-1 shadow-sm ring-1 ring-palette-emerald-border">
            <svg
              className="w-4 h-4 text-palette-emerald-badge"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Done!!
          </span>
        ) : (
          <Button
            variant="primary"
            size="sm"
            disabled={!canEdit || !allApproved || isCreating || isSearching}
            onClick={() => onCreateJournalEntry && onCreateJournalEntry(clinic, clinicRows)}
            title={
              !canEdit
                ? "You don't have permission to create journal entries"
                : allApproved
                  ? `Create NetSuite journal entry for ${clinic}`
                  : "All practitioners must be approved first"
            }
          >
            {isCreating ? "Creating..." : "Create Journal Entry"}
          </Button>
        )}
      </div>
      <DataTable
        columns={columnsWithSelect}
        data={clinicRows}
        rowKey={(r, i) => r.id ?? `${clinic}-${i}`}
        rowClassName={() => "hover:bg-palette-indigo-soft transition-colors"}
        renderPreHeaderCell={
          hasAnyTotal
            ? (col) => {
                const cfg = CLINIC_TOTAL_CONFIG[col.key];
                const totals = columnTotals[col.key];
                const allTotal = totals?.ALL ?? 0;
                const ddsTotal = totals?.DDS ?? 0;
                const hygTotal = totals?.HYG ?? 0;
                const showAll = cfg && Math.abs(allTotal) >= 0.005;
                const showDds = cfg && Math.abs(ddsTotal) >= 0.005;
                const showHyg = cfg && Math.abs(hygTotal) >= 0.005;
                if (!cfg || (!showAll && !showDds && !showHyg)) return null;
                const alignClass =
                  col.align === "center"
                    ? "items-center"
                    : col.align === "right"
                      ? "items-end"
                      : "items-start";
                return (
                  <div className={`flex flex-col gap-0.5 leading-none ${alignClass}`}>
                    {showAll && (
                      <span
                        className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold text-white normal-case ${cfg.badge}`}
                        title={`Clinic total · ${cfg.label}`}
                      >
                        {formatTotal(col.key, allTotal)}
                      </span>
                    )}
                    {showDds && (
                      <span
                        className="whitespace-nowrap rounded-full bg-palette-indigo-badge px-2 py-0.5 text-[10px] font-semibold text-white normal-case"
                        title={`DDS total · ${cfg.label}`}
                      >
                        DDS {formatTotal(col.key, ddsTotal)}
                      </span>
                    )}
                    {showHyg && (
                      <span
                        className="whitespace-nowrap rounded-full bg-palette-purple-badge px-2 py-0.5 text-[10px] font-semibold text-white normal-case"
                        title={`HYG total · ${cfg.label}`}
                      >
                        HYG {formatTotal(col.key, hygTotal)}
                      </span>
                    )}
                  </div>
                );
              }
            : undefined
        }
      />
    </div>
  );
}
